// pages/SpecialHRAListing.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  DollarSign,
  Search,
  Plus,
  RefreshCcw,
  Trash2,
  Calendar,
  Building2,
  User,
  Hash,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

const SpecialHRAListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // ✅ consistent button colors
  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  // filters
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [branch, setBranch] = useState("");

  // dropdown data
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);
  const [months] = useState([
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ]);

  // autocomplete suggestions
  const [empCodeSuggestions, setEmpCodeSuggestions] = useState([]);
  const [empNameSuggestions, setEmpNameSuggestions] = useState([]);

  // list
  const [rows, setRows] = useState([]);
  const [totalAmount, setTotalAmount] = useState("0.00");
  const [loading, setLoading] = useState(false);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-26", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (selectedIds.length === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700");

  // Check if Special HRA is enabled
  const checkFeatureEnabled = async () => {
    if (!String(finYearId).trim()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/special-hra-listing/check-enabled`, {
        params: { finYearId: Number(finYearId) },
      });

      if (res?.data?.success && !res.data.enabled) {
        toast.error("Special HRA Generation is not enabled");
        setTimeout(() => {
          window.location.href = "/module-home";
        }, 2000);
      }
    } catch (err) {
      console.error("Error checking feature:", err);
    }
  };

  // Load dropdown data
  const loadDropdowns = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    try {
      // Branches
      const branchRes = await axios.get(`${BASE_URL}/api/special-hra-listing/branches`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branchId,
          branchType: branchType,
        },
      });

      if (branchRes?.data?.success) {
        setBranches(branchRes.data.rows || []);
        // Set default branch to current user's branch
        setBranch(branchId);
      }

      // Years
      const yearRes = await axios.get(`${BASE_URL}/api/special-hra-listing/years`);
      if (yearRes?.data?.success) {
        setYears(yearRes.data.rows || []);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Failed to load dropdown data";
      toast.error(msg);
    }
  };

  // Employee Code Autocomplete
  const handleEmpCodeChange = async (value) => {
    setEmpCode(value);

    if (value.length >= 1 && branch) {
      try {
        const res = await axios.get(`${BASE_URL}/api/special-hra-listing/autocomplete/empcode`, {
          params: {
            finYearId: Number(finYearId),
            search: value,
            branchId: branch,
            branchType: branchType,
          },
        });

        if (res?.data?.success) {
          setEmpCodeSuggestions(res.data.rows || []);
        }
      } catch (err) {
        console.error("Error fetching employee codes:", err);
      }
    } else {
      setEmpCodeSuggestions([]);
    }
  };

  // Employee Name Autocomplete
  const handleEmpNameChange = async (value) => {
    setEmpName(value);

    if (value.length >= 1 && branch) {
      try {
        const res = await axios.get(`${BASE_URL}/api/special-hra-listing/autocomplete/empname`, {
          params: {
            finYearId: Number(finYearId),
            search: value,
            branchId: branch,
            branchType: branchType,
          },
        });

        if (res?.data?.success) {
          setEmpNameSuggestions(res.data.rows || []);
        }
      } catch (err) {
        console.error("Error fetching employee names:", err);
      }
    } else {
      setEmpNameSuggestions([]);
    }
  };

  // Get month name from value
  const getMonthName = (monthValue) => {
    const monthObj = months.find((m) => m.value === monthValue);
    return monthObj ? monthObj.label : "";
  };

  // Fetch listing
  const fetchList = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (!branch) {
      toast.warn("Please select Branch.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/special-hra-listing`, {
        params: {
          finYearId: Number(finYearId),
          branchId: branch,
          year: year,
          month: getMonthName(month),
          empCode: empCode.trim(),
          empName: empName.trim(),
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotalAmount(res.data.totalAmount || "0.00");

        // reset selection
        setSelectedIds([]);
        setSelectAll(false);
      } else {
        toast.error(res?.data?.message || "Failed to load listing.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkFeatureEnabled();
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search when branch is set
  useEffect(() => {
    if (branch) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    await fetchList();
  };

  const handleAddNew = () => {
    window.location.href = "/payroll/SpecialHRAgenerateEntry";
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }
    setSelectedIds(rows.map((r) => r.fld_specialhraid));
    setSelectAll(true);
  };

  const handleDeleteSelected = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (selectedIds.length === 0) {
      toast.warn("Please select any one");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      const res = await axios.post(
        `${BASE_URL}/api/special-hra-listing/delete-many`,
        { ids: selectedIds },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Successfully Removed");
        await fetchList();
      } else {
        toast.error(res?.data?.message || "Cannot delete selected records.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Something went wrong.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      {/* ✅ Toast outside container */}
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SPECIAL HRA GENERATE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <DollarSign className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Special HRA Management
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  View / Search / Delete Special HRA Records
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList()} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Search Filters */}
          <form onSubmit={handleSearch} className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Employee Code */}
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Hash className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empCode}
                    onChange={(e) => handleEmpCodeChange(e.target.value)}
                    placeholder="Search by code..."
                    className={`pl-11 ${inputBase}`}
                    list="empcode-suggestions"
                  />
                  {empCodeSuggestions.length > 0 && (
                    <datalist id="empcode-suggestions">
                      {empCodeSuggestions.map((code, idx) => (
                        <option key={idx} value={code} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>

              {/* Employee Name */}
              <div>
                <label className={labelBase}>Employee Name</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="w-4 h-4 text-gray-400" />
                  </div>
                  <input
                    value={empName}
                    onChange={(e) => handleEmpNameChange(e.target.value)}
                    placeholder="Search by name..."
                    className={`pl-11 ${inputBase}`}
                    list="empname-suggestions"
                  />
                  {empNameSuggestions.length > 0 && (
                    <datalist id="empname-suggestions">
                      {empNameSuggestions.map((name, idx) => (
                        <option key={idx} value={name} />
                      ))}
                    </datalist>
                  )}
                </div>
              </div>

              {/* Year */}
              <div>
                <label className={labelBase}>Year</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={`pl-11 ${inputBase}`}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={y.value} value={y.value}>
                        {y.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Month */}
              <div>
                <label className={labelBase}>Month</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={month} onChange={(e) => setMonth(e.target.value)} className={`pl-11 ${inputBase}`}>
                    <option value="Select">Select</option>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Branch */}
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="w-4 h-4 text-gray-400" />
                  </div>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className={`pl-11 ${inputBase}`}>
                    {branches.map((b, idx) => (
                      <option key={idx} value={b.fld_Branchid || b.value}>
                        {b.fld_BranchName || b.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Designation</th>
                    <th className="px-4 py-4 text-center font-bold">Branch Name</th>
                    <th className="px-4 py-4 text-center font-bold">Amount</th>
                    <th className="px-4 py-4 text-center font-bold">
                      <div className="flex items-center justify-center gap-2">
                        Select All
                        <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 5 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    <>
                      {rows.map((r, idx) => (
                        <tr
                          key={r.fld_specialhraid}
                          className={`${idx % 2 === 0 ? (isDark ? "bg-gray-900" : "bg-white") : (isDark ? "bg-gray-800" : "bg-gray-50")} hover:${
                            isDark ? "bg-gray-700" : "bg-gray-100"
                          }`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_employeecode}
                          </td>
                          <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_employee}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_designationname}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_branchname}
                          </td>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.fld_amount}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(r.fld_specialhraid)}
                              onChange={() => toggleSelect(r.fld_specialhraid)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      ))}

                      {/* Footer Row with Total */}
                      <tr className={`font-bold ${isDark ? "bg-gray-800" : "bg-gray-100"}`}>
                        <td colSpan={5} className={`px-4 py-4 text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          Total:
                        </td>
                        <td className={`px-4 py-4 text-center ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                          {totalAmount}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <button onClick={handleDeleteSelected} disabled={selectedIds.length === 0} className={dangerBtn}>
                            <Trash2 className="w-4 h-4" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SpecialHRAListing;